import HttpCodes from 'http-status-codes';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDidMount } from 'rooks';
import { Checkbox } from '@/core/Checkbox.atom';
import { FakeLink } from '@/core/FakeLink';
import { HoverTooltip } from '@/core/HoverTooltip.atom';
import { sqWorkbookStore } from '@/core/core.stores';
import { useFluxPath } from '@/core/hooks/useFluxPath.hook';
import { canManageItem, isAdmin, userHasExclusiveAccess } from '@/services/authorization.service';
import { getTableDefinitionStore } from '@/tableDefinitionEditor/store.utilities';
import { setTableDefinition } from '@/tableDefinitionEditor/tableDefinition.actions';
import { EDITOR_TYPES, batchActionOptions } from '@/tableDefinitionEditor/tableDefinition.constants';
import {
  createOrUpdateTableDefinition,
  tableDefinitionOutputToTableDefinitionInput,
  updateColumnsContainingRulesWithScope,
} from '@/tableDefinitionEditor/tableDefinition.utilities';
import { ItemScope } from '@/tools/itemProperties/ItemScope';
import { ScopeModal } from '@/tools/itemProperties/ScopeModal';
import { getScopeMessage } from '@/tools/itemProperties/propertiesPanel.utilities';
import { getItemACL } from '@/utilities/acl.utilities';
import { prettyPermissions } from '@/utilities/utilities';
import { sqTableDefinitionsApi } from '@seeqdev/api/endpoint/TableDefinitionsApi';
import { Icon, Modal } from '@seeqdev/qomponents';
import type { EditorType } from '@/tableDefinitionEditor/tableDefinition.types';
import type { TableDefinitionOutputV1 } from '@seeqdev/api/model/TableDefinitionOutputV1';

interface TableDefinitionSettingsModalProps {
  tableDefinition: TableDefinitionOutputV1;
  setShowEditPermissionsModal: (show: boolean) => void;
  onClose: () => void;
  editorType: EditorType;
}

export const TableDefinitionSettingsModal = ({
  tableDefinition,
  setShowEditPermissionsModal,
  onClose,
  editorType,
}: TableDefinitionSettingsModalProps) => {
  const { t } = useTranslation();
  const tableDefinitionStore = getTableDefinitionStore(editorType);

  const [showScopeModal, setShowScopeModal] = useState(false);
  const [exclusiveAccess, setExclusiveAccess] = useState<boolean>(false);
  const workbookId = useFluxPath(sqWorkbookStore, (store) => store.workbookId);
  const workbookName = useFluxPath(sqWorkbookStore, (store) => (editorType === 'ITEM' ? store.name : undefined));
  const showBatchAction = !!tableDefinitionStore.subscriberId;

  useDidMount(() => {
    setExclusiveAccess(false);
    getItemACL(tableDefinition.id)
      .then(({ entries }) => setExclusiveAccess(userHasExclusiveAccess(entries ?? [])))
      .catch(({ status }) => setExclusiveAccess(status !== HttpCodes.FORBIDDEN));
  });

  const canManageTableDefinitionAcl = canManageItem(tableDefinition);
  const getTableDefinitionAclLabel = () => {
    return `ACL_STATUS.${canManageTableDefinitionAcl ? 'ACCESS_CONTROL' : 'YOUR_PERMISSIONS'}`;
  };
  const getTableDefinitionAclAccess = () => {
    if (canManageTableDefinitionAcl) {
      if (isAdmin()) {
        return 'ACL_STATUS.ACCESS.ADMIN';
      } else {
        return `ACL_STATUS.ACCESS.${exclusiveAccess ? 'ONLY_YOU' : 'YOU_AND_OTHERS'}`;
      }
    } else {
      return prettyPermissions(tableDefinition.effectivePermissions || {}, 'ACL_STATUS.ACCESS.NO_PERMISSIONS');
    }
  };

  const updateColumnRuleScope = async (tableComputeUpdatedScope: TableDefinitionOutputV1) => {
    const tableDefinitionOutputV1 = await updateColumnsContainingRulesWithScope(
      tableComputeUpdatedScope,
      editorType,
      workbookId,
    );

    if (tableDefinitionOutputV1) {
      setTableDefinition(editorType, tableDefinitionOutputV1);
    } else {
      setTableDefinition(editorType, tableComputeUpdatedScope);
    }
  };

  const settingsTitle = editorType === 'SCALING' ? 'SCALING.TABLE_SETTING.TITLE' : 'ITEM_TABLE.TABLE_SETTING.TITLE';

  return (
    <>
      <Modal
        testId="tableDefinitionSettingsModal"
        title={t(settingsTitle)}
        open={true}
        onClose={onClose}
        hideFooterButtons={true}>
        <div>
          <div className="striped tw:pb-sq-5 tw:pl-sq-3">
            <div className="aclStatusWrapper flexRowContainer noBackground">
              <label className="specAclStatusLabel noBackground tw:mb-0">{t(getTableDefinitionAclLabel())}</label>
              <div className="flexColumnContainer noBackground">
                <div className="specAclStatusAccess">{t(getTableDefinitionAclAccess())}</div>
                <HoverTooltip text="ACL_STATUS.TOOLTIP.MANAGE_ACCESS_CONTROL">
                  <FakeLink
                    testId="editPermissionsButton"
                    extraClassNames="mr10"
                    onClick={() => setShowEditPermissionsModal(true)}>
                    <Icon icon="fa-user-group ml5" /> <span>{t('ACL_STATUS.MANAGE')}</span>
                  </FakeLink>
                </HoverTooltip>
              </div>
            </div>
          </div>

          <ItemScope
            canManage={canManageItem(tableDefinition)}
            availableOutsideAnalysis={!tableDefinition.scopedTo}
            scopeMessage={getScopeMessage(tableDefinition.scopedTo, workbookId, workbookName)}
            openScopeModal={() => setShowScopeModal(true)}
          />

          {showScopeModal && (
            <ScopeModal
              itemId={tableDefinition.id}
              closeModal={() => setShowScopeModal(false)}
              onScopeUpdate={async () => {
                const { data: tableComputeUpdatedScope } = await sqTableDefinitionsApi.getTableDefinition({
                  id: tableDefinition.id,
                });
                await updateColumnRuleScope(tableComputeUpdatedScope);
              }}
              workbookName={workbookName}
              warnAboutExistingAccess={editorType === EDITOR_TYPES.SCALING}
              itemType={editorType === EDITOR_TYPES.SCALING ? 'Scaling Table' : 'Tree Builder'}
            />
          )}

          {showBatchAction && (
            <div
              id="scalingTableBatchAction"
              className="flexRowContainer striped tw:mt-sq-5 tw:mb-sq-15 tw:pl-sq-3"
              data-testid="scalingTableBatchAction">
              <span className="tw:mb-sq-5">{t('SCALING.BATCH_ACTION.LABEL')}</span>
              <div className="flexRowContainer tw:ml-sq-5">
                {batchActionOptions.map((option) => {
                  return (
                    <Checkbox
                      key={option.value}
                      label={t(option.label)}
                      type="radio"
                      data-testid={option.value.toString()}
                      id={option.value.toString()}
                      value={option.value}
                      isChecked={tableDefinition.batchAction === option.value}
                      onClick={(e) => {
                        const tableDefUpdate = tableDefinitionOutputToTableDefinitionInput(
                          tableDefinition,
                          tableDefinitionStore.scopedTo,
                        );
                        tableDefUpdate.batchAction = option.value; // Update the batch action only
                        createOrUpdateTableDefinition(tableDefUpdate, tableDefinition.id).then(
                          (tableDefinitionOutput) => {
                            setTableDefinition(
                              editorType,
                              tableDefinitionOutput
                                ? tableDefinitionOutput
                                : { ...tableDefinition, batchAction: option.value },
                            );
                          },
                        );
                      }}
                    />
                  );
                })}
                {t('SCALING.BATCH_ACTION.DELETION_DESCRIPTION')}
              </div>
            </div>
          )}
        </div>
      </Modal>
    </>
  );
};
